// feedbackAdmin / dto — the wire contract of the operator's feedback list
// (the reports people sent with "Give feedback" and "Report this"). The zod
// schemas ARE the contract: the server parses each stored event with
// `feedbackReportSentRow` and answers with `feedbackAdminListDto`; the
// client parses that at its boundary (`@bilda/app/feedbackAdmin`).
// Browser-safe: zod, core and utils only.
//
// Facts, never display text: the time is epoch milliseconds, the kind and
// the contact consent are their domain values. The text is the person's
// own words, untrusted: shown as plain text, never as markup.
export { FEEDBACK_ADMIN_LIST_MAX, feedbackAdminListDto, feedbackReportSentRow, feedbackStatusRow, };
import { z } from "zod";
import { zFeedbackContextItem } from "../contract/commands/feedback.js";
// CL-26 {feedback-list-max} (QA-35): the newest reports one read answers
// with, on the operator's Feedback page and Overview. Older reports stay
// stored; paging through them is a later step.
const FEEDBACK_ADMIN_LIST_MAX = 200;
const zKind = z.enum(["problem", "idea", "question"]);
// One stored `feedback_report_sent` event, as the events table holds it.
// A row that does not parse is counted as unreadable, never shown half.
const feedbackReportSentRow = z.object({
    type: z.literal("feedback_report_sent"),
    workspaceId: z.string(),
    reportId: z.string(),
    reporter: z.string(),
    kind: zKind,
    text: z.string(),
    context: z.array(zFeedbackContextItem),
    contact: z.enum(["contact_allowed", "contact_not_allowed"]),
    at: z.number(),
});
// The newest status change of one report (`feedback_marked_handled` or
// `feedback_reopened`), as the events table holds it. A report with none is
// open. One that does not parse leaves the report open (shown, never lost).
const feedbackStatusRow = z.object({
    type: z.enum(["feedback_marked_handled", "feedback_reopened"]),
    reportId: z.string(),
});
const feedbackAdminReportDto = z.object({
    reportId: z.string(),
    // The workspace (tenant) the report is stored in: the id accounts and
    // AI usage name, and where its status changes go.
    workspaceId: z.string(),
    // The reporter's account email; null when the account is gone.
    reporterEmail: z.string().nullable(),
    kind: zKind,
    text: z.string(),
    context: z.array(zFeedbackContextItem),
    mayContact: z.boolean(),
    at: z.number(),
    // Open until the operator marks it handled (and again once reopened).
    status: z.enum(["open", "handled"]),
});
const feedbackAdminListDto = z.object({
    // Newest first, at most `FEEDBACK_ADMIN_LIST_MAX`.
    reports: z.array(feedbackAdminReportDto),
    // Stored reports this code could not read (shown as a count).
    unreadable: z.number().int().nonnegative(),
});
//# sourceMappingURL=dto.js.map